import { useState, useCallback, useRef, useEffect } from 'react';
import { TerminalState, HistoryEntry } from '../types';
import { findCommand, getCommandSuggestions } from '../utils/commandParser';

export const useTerminal = () => {
  const [state, setState] = useState<TerminalState>({
    history: [],
    currentCommand: '',
    commandHistory: [],
    historyIndex: -1,
    isTyping: false,
    theme: 'green',
    bootComplete: false
  });

  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const terminalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const addToHistory = useCallback((content: string, type: 'command' | 'output' = 'output') => {
    const entry: HistoryEntry = { content, type, timestamp: Date.now() };
    setState(prev => ({
      ...prev,
      history: [...prev.history, entry]
    }));
  }, []);

  const changeTheme = useCallback((theme: 'green' | 'amber' | 'blue' | 'red' | 'yellow' | 'purple') => {
    console.log('Changing theme from', state.theme, 'to', theme);
    setState(prev => ({ ...prev, theme }));
    console.log('Theme changed to:', theme);
  }, [state.theme]);

  // Add welcome message when terminal initializes
  useEffect(() => {
    console.log('useTerminal hook initialized, adding welcome message');
    addToHistory(`🚀 Welcome to Mohammad Tabish's Portfolio Terminal!

Type 'help' to see all available commands.
Try commands like: about, skills, projects, experience, education

💡 Pro Tips:
• Use Tab for command completion
• Arrow keys navigate command history
• Type 'clear' to reset the terminal
• Use 'theme' to change colors (green/amber/blue/red/yellow/purple)`, 'output');
  }, [addToHistory]);

  // Persist session state (theme and command history) in sessionStorage
  useEffect(() => {
    const savedState = sessionStorage.getItem('terminal-session-state');
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        setState(prev => ({
          ...prev,
          theme: parsed.theme || prev.theme,
          commandHistory: parsed.commandHistory || prev.commandHistory
        }));
        console.log('Restored session state:', parsed);
      } catch (error) {
        console.error('Failed to restore session state:', error);
      }
    }
  }, []);

  // Save session state whenever theme or command history changes
  useEffect(() => {
    const sessionState = {
      theme: state.theme,
      commandHistory: state.commandHistory
    };
    sessionStorage.setItem('terminal-session-state', JSON.stringify(sessionState));
  }, [state.theme, state.commandHistory]);

  const executeCommand = useCallback(async (input: string) => {
    if (!input.trim()) return;

    const command = input.trim();
    console.log('Executing command:', command);
    addToHistory(`guest@tabish-portfolio:~$ ${command}`, 'command');
    
    setIsProcessing(true);
    
    try {
      const cmd = findCommand(command);
      console.log('Found command:', cmd);
      if (cmd) {
        const args = command.split(' ').slice(1);
        console.log('Command args:', args);
        const result = await cmd.handler(args);
        console.log('Command result:', result);
        
        if (result.type === 'clear') {
          setState(prev => ({ ...prev, history: [] }));
        } else if (result.type === 'theme_change' && result.theme) {
          // Handle theme change - update state first, then add history
          const newTheme = result.theme as 'green' | 'amber' | 'blue' | 'red' | 'yellow' | 'purple';
          console.log('Attempting to change theme from', state.theme, 'to', newTheme);
          setState(prev => {
            console.log('Previous state theme:', prev.theme);
            const newState = { ...prev, theme: newTheme };
            console.log('New state theme:', newState.theme);
            return newState;
          });
          addToHistory(result.output, 'output');
          console.log('Theme change command completed');
        } else {
          addToHistory(result.output, 'output');
        }
      } else {
        console.log('Command not found:', command);
        addToHistory(`❌ Command '${command.split(' ')[0]}' not found. Type 'help' for available commands.`, 'output');
      }
    } catch (error) {
      console.error('Error executing command:', error);
      addToHistory(`❌ Error executing command: ${error}`, 'output');
    } finally {
      setIsProcessing(false);
    }

    // Add to command history
    setState(prev => ({
      ...prev,
      commandHistory: [...prev.commandHistory, command],
      historyIndex: -1,
      currentCommand: ''
    }));

    // Clear suggestions
    setSuggestions([]);

    // Auto-scroll to bottom after command execution
    setTimeout(() => {
      scrollToBottom();
    }, 100);

    // Refocus input after command execution
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 50);
  }, [addToHistory, changeTheme]);

  const handleInputChange = useCallback((value: string) => {
    setState(prev => ({ ...prev, currentCommand: value }));
    
    if (value.trim()) {
      const suggestions = getCommandSuggestions(value);
      setSuggestions(suggestions);
    } else {
      setSuggestions([]);
    }
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (state.currentCommand.trim()) {
        executeCommand(state.currentCommand);
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      if (suggestions.length > 0) {
        setState(prev => ({ 
          ...prev, 
          currentCommand: suggestions[0] 
        }));
        setSuggestions([]);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (state.commandHistory.length > 0 && state.historyIndex < state.commandHistory.length - 1) {
        const newIndex = state.historyIndex + 1;
        const command = state.commandHistory[state.commandHistory.length - 1 - newIndex];
        setState(prev => ({ 
          ...prev, 
          historyIndex: newIndex,
          currentCommand: command 
        }));
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (state.historyIndex > 0) {
        const newIndex = state.historyIndex - 1;
        const command = newIndex >= 0 ? state.commandHistory[state.commandHistory.length - 1 - newIndex] : '';
        setState(prev => ({ 
          ...prev, 
          historyIndex: newIndex,
          currentCommand: command 
        }));
      } else if (state.historyIndex === 0) {
        setState(prev => ({ 
          ...prev, 
          historyIndex: -1,
          currentCommand: '' 
        }));
      }
    }
  }, [state.currentCommand, state.commandHistory, state.historyIndex, suggestions, executeCommand]);

  const clearTerminal = useCallback(() => {
    setState(prev => ({ ...prev, history: [] }));
  }, []);

  const completeBoot = useCallback(() => {
    setState(prev => ({ ...prev, bootComplete: true }));
  }, []);

  const scrollToBottom = useCallback(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, []);

  return {
    state,
    suggestions,
    isProcessing,
    terminalRef,
    inputRef,
    addToHistory,
    executeCommand,
    handleInputChange,
    handleKeyDown,
    clearTerminal,
    changeTheme,
    completeBoot,
    scrollToBottom
  };
};
